import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  IconButton,
  Box,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import { Delete, BookmarkBorder } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { getUserWatched, addToWatchlist } from '../../services/watchlistApi';
import { unmarkAsWatched } from '../../services/movieInteractionApi';
import MovieDetailsDialog from './MovieDetailsDialog';

const UserWatchedList = () => {
  const { user } = useAuth();
  const { addNotification } = useApp();
  const [watchedList, setWatchedList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMovie, setSelectedMovie] = useState(null);
  const [movieDialogOpen, setMovieDialogOpen] = useState(false);

  useEffect(() => {
    if (user?.id) {
      fetchWatchedList();
    }
  }, [user?.id]);

  const fetchWatchedList = async () => {
    try {
      setLoading(true);
      const data = await getUserWatched(user.id);
      setWatchedList(data.items || []);
    } catch (err) {
      setError('Failed to load watched list');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (item) => {
    try {
      await unmarkAsWatched(user.id, item.movieID);
      setWatchedList(prev => prev.filter(w => w.id !== item.id));
      addNotification({
        type: 'success',
        message: `"${item.movie?.name}" removed from watched list`
      });
    } catch (err) {
      console.error('Error removing from watched list:', err);
      addNotification({
        type: 'error',
        message: 'Failed to remove from watched list'
      });
    }
  };

  const handleAddToWatchlist = async (item) => {
    try {
      const { addToWatchlist: addToWatchlistInteraction, unmarkAsWatched } = await import('../../services/movieInteractionApi');
      await addToWatchlistInteraction(user.id, item.movieID);
      await unmarkAsWatched(user.id, item.movieID);
      setWatchedList(prev => prev.filter(w => w.id !== item.id));
      addNotification({
        type: 'success',
        message: `"${item.movie?.name}" moved to watchlist`
      });
    } catch (err) {
      console.error('Error adding to watchlist:', err);
      addNotification({
        type: 'error',
        message: 'Failed to move to watchlist'
      });
    }
  };

  const handleMovieClick = (movie) => {
    setSelectedMovie(movie);
    setMovieDialogOpen(true);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Watched Movies
      </Typography>
      
      {watchedList.length === 0 ? (
        <Typography color="text.secondary" textAlign="center" py={4}>
          You haven't watched any movies yet
        </Typography>
      ) : (
        <Grid container spacing={3}>
          {watchedList.filter(item => item?.movie).map((item) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
              <Card sx={{ cursor: 'pointer' }}>
                <CardMedia
                  component="img"
                  height="300"
                  image={item.movie?.poster || '/placeholder-movie.svg'}
                  alt={item.movie?.name || 'Movie'}
                  onClick={() => handleMovieClick(item.movie)}
                  sx={{ '&:hover': { opacity: 0.8 } }}
                />
                <CardContent>
                  <Typography variant="h6" noWrap>
                    {item.movie?.name || 'Unknown Movie'}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={1}>
                    <Chip 
                      label="Watched" 
                      size="small" 
                      color="success" 
                      variant="filled" 
                    />
                    {item.movie?.rating && (
                      <Typography variant="body2" color="text.secondary">
                        ⭐ {item.movie.rating}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
                <CardActions>
                  <IconButton 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddToWatchlist(item);
                    }}
                    color="warning"
                    title="Move to watchlist"
                  >
                    <BookmarkBorder />
                  </IconButton>
                  <IconButton 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove(item);
                    }}
                    color="error"
                    title="Remove from watched"
                  >
                    <Delete />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      
      <MovieDetailsDialog
        open={movieDialogOpen}
        onClose={() => setMovieDialogOpen(false)}
        movieData={selectedMovie}
      />
    </Container>
  );
};

export default UserWatchedList;
